/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable complexity */
import cloneDeep from 'lodash/cloneDeep';
import { setTextRunColor, setTextRunStyles } from '@src/slide/domain/ui-model/element-modeles/property/text/text-operation';
import { getTextInParagraphList, updateFont } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import Border from '@src/slide/domain/ui-model/element-modeles/property/border';
import Fill from '@src/slide/domain/ui-model/element-modeles/property/fill';
import { BaseChartProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/base-property';
import { ChartActionType } from './constant';
import type { IBorderInfo, IBorderProperty } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import type {
    IChartTextProperty,
    IChartShapeProperty,
    NullableDeep, TextParaListOptions, TextBodyStyleOptions,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/chart-shape-property.interface';
import type { IChartActionType } from './constant';
import type { IBackgroundFillInfo, IFillProperty } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import type { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type {
    ShapeProps,
    TextProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { IParagraphStyle, ITextBodyStyle, ITextRunStyle } from '../text/interface/editor-styles.interface';
import type { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import UpdateAndRemoveLogic from '../fill/update-remove';
import { IAllStyles } from '../text/interface/editor-styles.interface';
import stylesMerge from '@src/slide/domain/ui-model/element-modeles/property/text/utils/style-merge';
import {
    CHART_DEFAULT_PADDING_LR,
    CHART_DEFAULT_PADDING_T,
    CHART_DEFAULT_PADDING_B,
} from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { ITDColor, TDColor } from '../color/td-color';
import { Effect } from '@src/slide/domain/ui-model/element-modeles/property/effect';
import { IEffectModelValCollection, IEffect, IEffectUpdateRemove, EEffectType } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';


export default abstract class ChartShapeProperty<T extends ShapeProps = ShapeProps>
    extends BaseChartProperty<T> implements IChartShapeProperty {
    public get props() {
        return this.uiData.props;
    }

    protected borderInstance: IBorderProperty;
    protected fillInstance: IFillProperty;
    protected effectInstance: IEffect;

    public getBorder(): IBorderProperty {
        if (!this.borderInstance && this.uiData) {
            this.borderInstance = new Border(this.uiData as unknown as IElementBaseModel);
        }
        return this.borderInstance;
    }

    public getBackgroundFill(): IFillProperty {
        if (!this.fillInstance) {
            this.fillInstance = new Fill(this.uiData as unknown as IElementBaseModel);
        }
        return this.fillInstance;
    }

    public getEffect(): IEffect {
        if (!this.effectInstance) {
            this.effectInstance = new Effect(this.uiData as unknown as IElementBaseModel);
        }
        return this.effectInstance;
    }

    public updateBorder(borderSet: IBorderInfo): void {
        const props = { style: { border: borderSet } };
        const updateAndRemove = UpdateAndRemoveLogic.convertToUpdateAndRemove(props, this.uiData.props);
        const updateProps = { props: updateAndRemove.update };
        const removeProps = updateAndRemove.remove ? { props: updateAndRemove.remove } : undefined;
        this.updateProps(updateProps as T, removeProps as NullableDeep<T>);
    }

    public updateFill(fillInfo: IBackgroundFillInfo): void {
        const props = { style: { fillBackground: fillInfo } };
        const updateAndRemove = UpdateAndRemoveLogic.convertToUpdateAndRemove(props, this.uiData.props);
        const updateProps = { props: updateAndRemove.update };
        const removeProps = updateAndRemove.remove ? ({ props: updateAndRemove.remove }) : undefined;
        this.updateProps(updateProps  as T, removeProps as NullableDeep<T>);
    }

    public updateEffect(effectInfo: IEffectModelValCollection): void {
        const updateAndRemove = Effect.getEffectUpdateRemove(effectInfo);
        const updateProps = { props: updateAndRemove.update };
        const removeProps = updateAndRemove.remove ? ({ props: updateAndRemove.remove }) : undefined;
        this.updateProps(updateProps as T, removeProps as NullableDeep<T>);
    }

    public abstract updateProps(updateProps: Partial<T>, removeProps?: Partial<NullableDeep<T>> | null): void;
}

export abstract class ChartTextProperty<T extends TextProps> extends ChartShapeProperty<T>
    implements IChartTextProperty<T> {
    /**
     * 获取富文本文本数据
     */
    public get text() {
        return this.uiData.text;
    }

    public get paragraphList() {
        return this.uiData.paragraphList;
    }

    public get textBodyStyle() {
        return this.uiData.textBodyStyle;
    }

    /**
     * 获取文本对应的段落列表信息。
     * 获取原则，参考office：
     * 1. 无富文本：使用默认run文本样式，使用对应默认字符串生成段落信息
     * 2. 有富文本：使用富文本段落信息，注意：这里面run不需要合并defaultParaList中默认run样式，office也不会合并
     * @param opts 生成段落信息的一些默认值传参
     */
    public getParagraphList(opts?: TextParaListOptions): IParagraph[] {
        const { text: richText, paragraphList: defaultParaList } = this;
        const { content = '', textAlign = 'left', level = 0 } = opts || { };
        if (!richText) { // 无富文本，使用默认样式
            return [
                {
                    ...defaultParaList?.[0],
                    paragraphProps: {
                        textAlign,
                        ...defaultParaList?.[0]?.paragraphProps,
                        level,
                    },
                    textRuns: [
                        {
                            text: content,
                            styles: {
                                ...defaultParaList?.[0]?.paragraphProps?.defaultRunProps,
                            },
                            type: 'run',
                        },
                    ],
                },
            ];
        }
        // 有富文本，使用富文
        return (
            richText.paragraphList?.map(paragraph => ({
                ...paragraph,
                paragraphProps: {
                    textAlign,
                    ...paragraph?.paragraphProps,
                    level,
                },
                textRuns: paragraph.textRuns?.map(run => ({
                    ...run,
                    styles: {
                        ...paragraph.paragraphProps?.defaultRunProps,
                        ...run.styles,
                    },
                })),
            })) || []
        );
    }

    /**
     * 获取text body样式，
     * 参照office：有富文本使用富文本的body style，否则使用默认body style
     */
    public getTextBodyStyle(opts?: TextBodyStyleOptions): ITextBodyStyle {
        const { noDefaultPadding = false, anchorCenter = false, alignItems } = opts || { };
        const { text: richText, textBodyStyle: defaultBodyStyle } = this;
        let textBodyStyle = defaultBodyStyle;
        if (richText) {
            textBodyStyle = richText.textBodyStyle;
        }
        const defaultPaddingT = noDefaultPadding ? 0 : CHART_DEFAULT_PADDING_T;
        const defaultPaddingB = noDefaultPadding ? 0 : CHART_DEFAULT_PADDING_B;
        const defaultPaddingLR = noDefaultPadding ? 0 : CHART_DEFAULT_PADDING_LR;

        const { bodyStyle } = textBodyStyle || { };
        const paddingLeft = bodyStyle?.paddingLeft ?? defaultPaddingLR;
        const paddingTop = bodyStyle?.paddingTop ?? defaultPaddingT;
        const paddingRight = bodyStyle?.paddingRight ?? defaultPaddingLR;
        const paddingBottom = bodyStyle?.paddingBottom ?? defaultPaddingB;
        const newBodyStyle = { ...bodyStyle, paddingLeft, paddingRight, paddingTop, paddingBottom  };
        textBodyStyle = { ...textBodyStyle, bodyStyle: newBodyStyle };

        const { wrapperStyle } = textBodyStyle || { };
        const newWrapperStyle = { ...wrapperStyle, justifyContent: anchorCenter ? 'center' : 'start'  };
        if (alignItems) {
            newWrapperStyle.alignItems = alignItems;
        }
        textBodyStyle = { ...textBodyStyle, wrapperStyle: newWrapperStyle };
        return textBodyStyle;
    }

    public getTextContent(): string {
        const paragraphList = this.getParagraphList();
        return getTextInParagraphList(paragraphList);
    }

    public getTextFontFamily(): string {
        const styles = this.getTextStyles();
        return styles?.fontFamily
            || styles?.fonts?.ea
            || styles?.fonts?.latin
            || (styles?.fonts as string)
            || '';
    }

    public getTextFontColor(): ITDColor {
        const styles = this.getTextStyles();
        return styles?.tdColor || new TDColor();
    }

    public getTextFontSize(): number {
        const styles = this.getTextStyles();
        return styles?.fontSize || 18;
    }

    public getTextFontBold(): boolean {
        const styles = this.getTextStyles();
        return styles?.fontWeight === 'bold';
    }

    public getTextFontItalic(): boolean {
        const styles = this.getTextStyles();
        return styles?.fontStyle === 'italic';
    }

    public getUpdatedTextProps(actionType: IChartActionType, value): TextProps {
        const { defParaList, richParaList  } = this.createUpdatedText(actionType, value);
        return this.createUpdatedProps(defParaList, richParaList);
    }

    public updateFontStyle(actionType: IChartActionType, value): void {
        const { defParaList, richParaList  } = this.createUpdatedText(actionType, value);
        this.updateParagraphList(defParaList, richParaList);
    }

    /**
     * 更新文本
     * @param paragraphList
     * @param richParagraphList  富文本段落
     */
    public updateParagraphList(paragraphList?: IParagraph[], richParagraphList?: IParagraph[]): void {
        const updateProps: Partial<T> = this.createUpdatedProps(paragraphList, richParagraphList);
        this.updateProps(updateProps);
    }

    protected getUpdatedParagraphLsit(paragraphList, actionType: IChartActionType, value) {
        let nP: IParagraph[] = [];
        switch (actionType) {
            case ChartActionType.FONTFAMILY: {
                nP = updateFont(cloneDeep(paragraphList), value);
                break;
            }
            case ChartActionType.FONT_COLOR: {
                if (value?.srgb_color) value.srgb_color = value.srgb_color.toUpperCase?.();
                nP = setTextRunColor(paragraphList, value);
                break;
            }
            case ChartActionType.FONT_SIZE: {
                const n = value.match(/-([\d|\\.]*)pt/);
                if (!n) return;
                const size = Number(n[1]);
                nP = setTextRunStyles(paragraphList, { fontSize: size });
                break;
            }
            case ChartActionType.FONT_SIZE_UP:
            case ChartActionType.FONT_SIZE_DOWN: {
                const increase = value.size;
                const min = value.min || 8;
                const max = value.max || 192;
                nP = this.setFontSizeIncrease(
                    paragraphList,
                    increase,
                    min, max,
                );
                break;
            }
            case ChartActionType.BOLD: {
                nP = setTextRunStyles(paragraphList, { fontWeight: (value && 'bold') || 'normal' });
                break;
            }
            case ChartActionType.ITALIC: {
                nP = setTextRunStyles(paragraphList, { fontStyle: (value && 'italic') || 'normal' });
                break;
            }
            default:
                break;
        }
        return nP;
    }

    protected getTextStyles(): IAllStyles | undefined {
        const paragraphList = this.getParagraphList();
        const textBodyStyle = this.getTextBodyStyle();
        const allStyleList: IAllStyles[] = [];
        Array.isArray(paragraphList) && paragraphList.forEach((para) => {
            const { paragraphProps = { }, textRuns } = para;
            textRuns?.forEach((run) => {
                const { styles = { } } = run;
                allStyleList.push(this.combineStyles(textBodyStyle, paragraphProps, styles));
            });
        });
        return stylesMerge(allStyleList);
    }

    // 字体自增自减
    protected setFontSizeIncrease(paragraphList, increaseSize, min, max) {
        return paragraphList.map((para: IParagraph, i) => {
            let newSize = this.getTextFontSize() + increaseSize;
            if (newSize > max) newSize = max;
            if (newSize < min) newSize = min;
            para.textRuns = para.textRuns?.map((textRun, j) => {
                textRun.styles = { ...textRun.styles };
                textRun.styles.fontSize = newSize;
                textRun.styles['raw-fontSize'] = true;

                return textRun;
            });
            if (para.paragraphProps?.defaultRunProps) {
                const { defaultRunProps } = para.paragraphProps;
                defaultRunProps.fontSize = newSize;
            }
            return para;
        });
    }

    protected createDefaultParagraphList() {
        return [{
            paragraphProps: {
                defaultRunProps: {},
            },
            textRuns: [],
        }];
    }

    private createUpdatedText(actionType: IChartActionType, value):
    { defParaList?: IParagraph[], richParaList?: IParagraph[] } {
        const { text: richText } = this;
        let { paragraphList: defaultParaList = [] } = this;
        const richParaList = richText?.paragraphList;
        if (!defaultParaList?.length && !richParaList) {  // 有富文本和并且没有默认段落样式，只需要改富文本样式即可
            defaultParaList = this.createDefaultParagraphList();
        }
        const updatedParagraphList = this.getUpdatedParagraphLsit(defaultParaList, actionType, value);
        // 处理富文本变更
        let richUpdatedParagraphList;
        if (richParaList?.length) {
            richUpdatedParagraphList = this.getUpdatedParagraphLsit(richParaList, actionType, value);
        }
        return { defParaList: updatedParagraphList,  richParaList: richUpdatedParagraphList };
    }

    private createUpdatedProps(paragraphList?: IParagraph[], richParagraphList?: IParagraph[]) {
        const updateProps: Partial<T> = { };
        if (paragraphList?.length) {
            updateProps.paragraphList = paragraphList;
        }
        if (richParagraphList?.length) {
            updateProps.text = { paragraphList: richParagraphList };
        }
        return updateProps;
    }

    private combineStyles(
        textBodyStyle: ITextBodyStyle,
        paraStyle: IParagraphStyle,
        runStyle: ITextRunStyle,
    ): IAllStyles {
        return {
            textAlign: 'left',
            PPTLineHeight: 1,
            lineHeight: 1.2,
            ...runStyle,
            ...paraStyle,
            ...textBodyStyle?.wrapperStyle,
            ...textBodyStyle?.bodyStyle,
        };
    }
}

/**
 * 图表线条Property，与ShapeProperty数据结构完全一致，多了边框继承的逻辑
 */
export abstract class ChartLineProperty<T extends ShapeProps = ShapeProps> extends ChartShapeProperty<T> {
    protected defaultLineFill?: string;
}
